/**
 * The Order and wait PO page (BLU-1506). Blanks bought ahead from an Order and
 * wait supplier, for orders that wait for them. The page answers three
 * questions: where is the box (Not sent yet / On the way / Late / Arrived),
 * what is on it, and which orders wait for it.
 *
 * Built from the just-in-time page's pieces (the buy-list action bar, the
 * TopHeader title, BackToTopPill) and none of the receive machinery: the box
 * goes straight to the floor, nothing is shelved or counted as stock.
 *
 *   Not sent yet  Send order (the Send dialog: mark blanks out, then send)
 *   On the way    Arrived (the Arrived sheet: what came in the box)
 *   Late          the same, the status amber
 *   Arrived       read only, cost only
 *
 * The header comes from the PO itself (`getOrder`); the lines, the out and
 * short marks and the orders come from the floor owner's Order and wait read.
 * Anyone else sees the header and a plain note, never an error.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCheck, ClipboardList, Send, Truck, X, type LucideIcon } from 'lucide-react';
import type { OrderAndWaitOrderRow, OrderAndWaitPo } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { NoteLine } from '@/components/shared/NoteLine';
import { SectionError } from '@/components/shared/SectionError';
import { WarningNotice } from '@/components/shared/WarningNotice';
import RoleGuard from '@/components/auth/RoleGuard';
import { BackToTopPill } from '@/components/BackToTopPill';
import { usePageTitle } from '@/contexts/PageTitleContext';
import { useRole } from '@/hooks/use-role';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { useLocations } from '@/features/locations/hooks/useLocations';
import { waitingOnBlankHref } from '@/features/production-orders/utils/waiting-on-blank';
import { formatDay, useDateFnsLocale } from '@/lib/format-dates';
import { cn } from '@/lib/utils';
import type { PurchaseOrder } from '@/features/purchase-orders';
import { useOrderAndWaitPo } from '../hooks/useOrderAndWait';
import {
  ARRIVED_BUTTON_TONE,
  ORDER_AND_WAIT_BAR_BADGE_TONE,
  floorToday,
  formatSendTime,
  orderAndWaitPoStatus,
  orderAndWaitStatusLabel,
  orderAndWaitStatusTone,
  sumQty,
} from '../lib/order-and-wait';
import { jitRowName } from '../lib/jit-lines';
import { PoActionBar } from './PoActionBar';
import { OrderAndWaitLineMarks } from './OrderAndWaitLineMarks';
import { OrderAndWaitStatusChip } from './OrderAndWaitStatusChip';
import { SendOrderDialog } from './SendOrderDialog';
import { ArrivedDialog } from './ArrivedDialog';

/** Roles the Order Queue admits (App.tsx `production/orders`); anyone else gets no link into it. */
const QUEUE_ROLES: ReadonlySet<string> = new Set(['Super Admin', 'Admin', 'Warehouse Coordinator']);

/** The bar's status glyph per display status, so colour is never the only signal (the PO bars' own icons). */
const BAR_STATUS_ICON: Record<string, LucideIcon> = {
  not_sent: ClipboardList,
  on_the_way: Truck,
  late: AlertTriangle,
  arrived: CheckCheck,
  cancelled: X,
};

const BAR_BUTTON = 'h-10 gap-1.5 px-3 text-xs sm:h-8';
const PRIMARY = cn(BAR_BUTTON, 'bg-threadheads-purple text-white hover:bg-threadheads-purple/90');
const ARRIVED = cn(BAR_BUTTON, ARRIVED_BUTTON_TONE);

interface OrderAndWaitOrderViewProps {
  order: PurchaseOrder;
  /** The PO list URL to go back to (filters kept). */
  ordersReturnTo: string;
}

export function OrderAndWaitOrderView({ order, ordersReturnTo }: OrderAndWaitOrderViewProps) {
  const { t } = useTranslation('orders');
  const locale = useDateFnsLocale();
  const { role, can, isAdmin } = useRole();
  const canManage = can('purchase_orders.manage');
  // Lead times live on the supplier page, which only an admin may change.
  const canSetLeadTime = isAdmin();
  const { allLocations } = useLocations();
  const floor = allLocations.find((location) => location.id === order.location_id) ?? null;
  const oaw = useOrderAndWaitPo(order.location_id ?? null, order.id);
  const po = oaw.purchaseOrder;
  const [sendOpen, setSendOpen] = useState(false);
  const [arrivedOpen, setArrivedOpen] = useState(false);

  const supplierName = po?.supplierName ?? order.supplier_name ?? null;
  const supplierId = po?.supplierId ?? order.supplier_id ?? null;
  usePageTitle(
    order.po_number,
    supplierName ?? t('oaw.yourSupplier'),
    useMemo(() => ({ label: t('jit.back'), to: ordersReturnTo }), [t, ordersReturnTo]),
  );

  const status = order.status;
  const displayStatus =
    po?.displayStatus ?? orderAndWaitPoStatus(status, order.arrival_eta_date, floorToday(floor?.timezone));
  const dueDate = po?.arrivalEtaDate ?? order.arrival_eta_date ?? null;
  const hasLines = (po?.lines.length ?? 0) > 0;

  const actions =
    canManage && po ? (
      status === 'draft' ? (
        <Button type="button" className={PRIMARY} onClick={() => setSendOpen(true)} disabled={!hasLines}>
          <Send className="h-3.5 w-3.5" aria-hidden="true" />
          {t('oaw.card.send')}
        </Button>
      ) : status === 'submitted' ? (
        <Button type="button" variant="outline" className={ARRIVED} onClick={() => setArrivedOpen(true)}>
          <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
          {t('oaw.card.arrived')}
        </Button>
      ) : null
    ) : null;

  // The bar's status badge: the other PO bars' Badge, in this status's colour and words.
  const statusLabel = orderAndWaitStatusLabel(t, displayStatus, {
    supplierName,
    dueDate: displayStatus === 'on_the_way' || displayStatus === 'late' ? dueDate : null,
    locale,
  });
  const statusStyle = {
    className: ORDER_AND_WAIT_BAR_BADGE_TONE[orderAndWaitStatusTone(displayStatus)],
    icon: BAR_STATUS_ICON[displayStatus] ?? ClipboardList,
  };

  return (
    <RoleGuard requirePurchaseOrderAccess>
      <div className="flex flex-col">
        <main className="w-full flex-1 px-4 py-6 sm:px-6 lg:px-8">
          <PoActionBar
            order={order}
            justInTime={{
              kind: 'order_and_wait',
              statusLabel,
              statusStyle,
              ariaLabel: t('oaw.bar.label'),
              batch: null,
              actions,
            }}
          />

          <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
            <div className="min-w-0 space-y-6">
              {oaw.loading ? (
                <div className="space-y-3" role="status" aria-label={t('oaw.page.loading')}>
                  <Skeleton className="h-40 rounded-lg" />
                  <Skeleton className="h-28 rounded-lg" />
                </div>
              ) : oaw.forbidden ? (
                <WarningNotice role="status">
                  {t('oaw.page.notOwner', { floor: floor?.name ?? t('toBuy.thisFloor') })}
                </WarningNotice>
              ) : oaw.notFound ? (
                <WarningNotice role="status">{t('oaw.page.notFound')}</WarningNotice>
              ) : oaw.failed || !po ? (
                <SectionError
                  title={t('oaw.page.failed')}
                  onRetry={oaw.retry}
                  retryLabel={t('jit.retry')}
                  retrying={oaw.retrying}
                />
              ) : (
                <>
                  <LinesCard po={po} currency={order.currency ?? po.currency} />
                  <OrdersCard
                    po={po}
                    orders={oaw.orders}
                    total={oaw.total}
                    hasMore={oaw.hasMore}
                    queueHref={QUEUE_ROLES.has(role ?? '') && status !== 'received' ? waitingOnBlankHref(po.poNumber) : null}
                  />
                </>
              )}
            </div>

            <aside className="min-w-0 space-y-6">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">{t('oaw.page.details')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-2 text-sm">
                    <dt className="text-muted-foreground">{t('oaw.page.supplier')}</dt>
                    <dd className="min-w-0 break-words">
                      {supplierId ? (
                        <Link
                          to={`/suppliers/${supplierId}`}
                          className="font-medium text-primary-strong underline-offset-4 hover:underline"
                        >
                          {supplierName ?? t('oaw.yourSupplier')}
                        </Link>
                      ) : (
                        supplierName ?? t('oaw.yourSupplier')
                      )}
                    </dd>
                    <dt className="text-muted-foreground">{t('oaw.page.floor')}</dt>
                    <dd className="min-w-0 break-words">{floor?.name ?? '-'}</dd>
                    <dt className="text-muted-foreground">{t('oaw.page.howSent')}</dt>
                    <dd className="min-w-0">
                      {po?.sendMode === 'daily' && po.sendTime
                        ? t('oaw.page.sendDaily', { time: formatSendTime(po.sendTime, locale) })
                        : t('oaw.page.sendWhenPressed')}
                    </dd>
                    <dt className="text-muted-foreground">{t('oaw.page.sent')}</dt>
                    <dd className="tabular-nums">
                      {order.submitted_date ? formatDay(order.submitted_date, locale) : t('oaw.page.notYet')}
                    </dd>
                    <dt className="text-muted-foreground">{t('oaw.page.due')}</dt>
                    <dd className="min-w-0 tabular-nums">
                      {dueDate ? (
                        formatDay(dueDate, locale)
                      ) : status === 'submitted' ? (
                        <span className="text-muted-foreground">
                          {t('oaw.page.noDue')}{' '}
                          {supplierId && canSetLeadTime && (
                            <Link
                              to={`/suppliers/${supplierId}`}
                              className="font-medium text-primary-strong underline-offset-4 hover:underline"
                            >
                              {t('oaw.card.setLeadTime')}
                            </Link>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{t('oaw.page.dueOnSend')}</span>
                      )}
                    </dd>
                    {status === 'received' && (
                      <>
                        <dt className="text-muted-foreground">{t('oaw.page.arrivedOn')}</dt>
                        <dd className="tabular-nums">{formatDay(order.received_date ?? po?.receivedDate, locale)}</dd>
                      </>
                    )}
                  </dl>
                  <NoteLine as="p" className="mt-4">
                    {t('oaw.page.notStock')}
                  </NoteLine>
                </CardContent>
              </Card>
            </aside>
          </div>
        </main>
        <BackToTopPill />
      </div>

      <SendOrderDialog open={sendOpen} onOpenChange={setSendOpen} po={status === 'draft' ? po : null} />
      <ArrivedDialog
        open={arrivedOpen}
        onOpenChange={setArrivedOpen}
        po={status === 'submitted' ? po : null}
        locationId={order.location_id ?? null}
        nextPoNumber={null}
      />
    </RoleGuard>
  );
}

function LinesCard({ po, currency }: { po: OrderAndWaitPo; currency: string | null }) {
  const { t } = useTranslation('orders');
  const { format } = useCurrencyFormatter();
  const money = (value: number | null) => (value === null ? '-' : format(value, currency));
  const totalUnits = sumQty(po.lines);
  const totalCost = po.totalCost ?? po.lines.reduce((sum, line) => sum + (line.lineCost ?? 0), 0);

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{t('oaw.page.lines')}</CardTitle>
      </CardHeader>
      {po.lines.length === 0 ? (
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('oaw.card.empty')}</p>
        </CardContent>
      ) : (
        <table className="w-full table-fixed text-sm">
          <caption className="sr-only">{t('oaw.page.linesCaption', { po: po.poNumber })}</caption>
          <thead>
            <tr className="border-y text-[11px] uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="px-4 py-2 text-left font-medium sm:px-6">{t('oaw.col.blank')}</th>
              <th scope="col" className="hidden w-52 px-3 py-2 text-left font-medium md:table-cell">{t('oaw.col.status')}</th>
              <th scope="col" className="w-16 px-3 py-2 text-right font-medium">{t('oaw.col.qty')}</th>
              <th scope="col" className="hidden w-24 px-3 py-2 text-right font-medium sm:table-cell">{t('oaw.col.unitCost')}</th>
              <th scope="col" className="w-24 px-4 py-2 text-right font-medium sm:px-6">{t('oaw.col.lineCost')}</th>
            </tr>
          </thead>
          <tbody>
            {po.lines.map((line, index) => (
              <tr key={line.skuId ?? `${line.sku}-${index}`} className="border-b align-top">
                <td className="px-4 py-2 sm:px-6">
                  <p className="break-words">{jitRowName(line)}</p>
                  {line.sku && <p className="break-all font-mono text-[11px] text-muted-foreground">{line.sku}</p>}
                  <OrderAndWaitLineMarks line={line} supplierName={po.supplierName} showWaiting className="mt-1 md:hidden" />
                </td>
                <td className="hidden px-3 py-2 md:table-cell">
                  <OrderAndWaitLineMarks line={line} supplierName={po.supplierName} showWaiting />
                </td>
                <td className="px-3 py-2 text-right tabular-nums">{line.qty}</td>
                <td className="hidden px-3 py-2 text-right tabular-nums sm:table-cell">{money(line.unitCost)}</td>
                <td className="px-4 py-2 text-right tabular-nums sm:px-6">{money(line.lineCost)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="font-medium">
              <th scope="row" className="px-4 py-2 text-left sm:px-6">{t('oaw.page.total')}</th>
              <td className="hidden md:table-cell" />
              <td className="px-3 py-2 text-right tabular-nums">{totalUnits}</td>
              <td className="hidden sm:table-cell" />
              <td className="px-4 py-2 text-right tabular-nums sm:px-6">{money(totalCost)}</td>
            </tr>
          </tfoot>
        </table>
      )}
    </Card>
  );
}

function OrdersCard({
  po,
  orders,
  total,
  hasMore,
  queueHref,
}: {
  po: OrderAndWaitPo;
  orders: readonly OrderAndWaitOrderRow[];
  total: number;
  hasMore: boolean;
  queueHref: string | null;
}) {
  const { t } = useTranslation('orders');
  const locale = useDateFnsLocale();

  return (
    <Card className="overflow-hidden">
      <CardHeader className="flex flex-row flex-wrap items-baseline justify-between gap-2 space-y-0 pb-3">
        <CardTitle className="text-base">{t('oaw.page.orders', { count: total })}</CardTitle>
        {queueHref && orders.length > 0 && (
          <Link to={queueHref} className="text-sm font-medium text-primary-strong underline-offset-4 hover:underline">
            {t('oaw.page.openQueue')}
          </Link>
        )}
      </CardHeader>
      {orders.length === 0 ? (
        <CardContent>
          <p className="text-sm text-muted-foreground">{t('oaw.page.noOrders')}</p>
        </CardContent>
      ) : (
        <ul className="divide-y border-t">
          {orders.map((row) => {
            const blanks = row.blanks.filter((blank) => blank.poId === po.poId);
            return (
              <li key={row.prodOrderId} className="flex flex-wrap items-start gap-x-4 gap-y-1 px-4 py-2.5 sm:px-6">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">
                    {row.orderName ?? t('oaw.page.unnamedOrder')}
                    {row.orderDate && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground tabular-nums">
                        {formatDay(row.orderDate, locale)}
                      </span>
                    )}
                  </p>
                  <ul className="mt-0.5 text-xs text-muted-foreground">
                    {blanks.map((blank, index) => (
                      <li key={`${blank.skuId ?? blank.sku}-${index}`} className="break-words">
                        {t('oaw.page.blankLine', { count: blank.qty, name: jitRowName(blank) })}
                      </li>
                    ))}
                  </ul>
                </div>
                <OrderAndWaitStatusChip status={row.status} supplierName={po.supplierName} dueDate={row.arrivalEtaDate} />
              </li>
            );
          })}
        </ul>
      )}
      {hasMore && (
        <CardContent className="border-t pt-3">
          <NoteLine as="p">{t('oaw.page.more', { shown: orders.length, total })}</NoteLine>
        </CardContent>
      )}
    </Card>
  );
}
